import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import type {SelectMenuOption} from 'shells/action-menu/action-menu';
import {ErrorState} from 'shells/error-state/error-state';
// @ts-expect-error - imported from .js/.jsx file
import {CheckmarkIcon} from 'shells/icon/svg/checkmark-icon';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {FetchMoreButton} from 'shells/table/fetch-more-button';
import {LegacySetupTableContainer} from 'shells/table/legacy-setup-table-container';
import {LegacySetupTableEmptyState} from 'shells/table/legacy-setup-table-empty-state';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {Table} from 'shells/table/table';
import {AddButtonRow} from 'shells/table/table-body/add-button-row';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {TwoLine} from 'shells/two-line/two-line';
import {Body} from 'shells/typography/body';

import type {GetUsersByFilter_usersByFilter_edges_node as User} from 'nutshell-graphql-types';

import {useGetInstanceInfo} from '../../../core/use-get-instance-info';
import {useGetSessionUser} from '../../../core/use-get-session-user';

// @ts-expect-error
import {useGetLicenseOverview} from '../../licensing/graphql/hooks';

import {UNLICENSED_USERS_TITLE} from './helpers';
import {LicenseToggleComponent} from './users-table-license-cell';

type Props = {
    users: User[];
    isLoading: boolean;
    errorMessage: string | null | undefined;
    hasNextPage: boolean;
    totalCount: number;
    fetchMore: () => void;
    title: string;
    emptyStateMessage: string;
    actionMenuOptions?: SelectMenuOption[];
    onAddRowClick?: () => void;
    refetchUsers: () => Promise<any>;
};

const LICENSE_COLUMNS_START_INDEX = 3;
const TWO_FACTOR_COLUMN_INDEX = 6;

export function UsersTable(props: Props) {
    const {
        title,
        users,
        isLoading,
        errorMessage,
        fetchMore,
        hasNextPage,
        totalCount,
        emptyStateMessage,
        actionMenuOptions,
        refetchUsers,
    } = props;

    const {licenseOverview, refetch: refetchLicenseOverview} = useGetLicenseOverview();
    const {user: sessionUser} = useGetSessionUser();
    const {sessionInstance} = useGetInstanceInfo();

    const isTrial = sessionInstance && sessionInstance.isTrial;
    const licenses = licenseOverview ? licenseOverview.instanceLicenses : [];

    const columns = React.useMemo(() => {
        const columnsArray = [
            {
                id: 'user',
                Header: 'User',
                // @ts-expect-error
                accessor: (row) => row,
                dataType: 'component',
                width: 200,
                Cell: ({cell}: any) => {
                    const email =
                        cell.value.emailAddresses && cell.value.emailAddresses.length > 0
                            ? cell.value.emailAddresses[0].address
                            : '';

                    return (
                        <div className='truncate'>
                            <Link
                                // @ts-expect-error
                                as={RoutedLink}
                                size='small'
                                variant='secondary'
                                to={cell.value.redirectUrl}
                            >
                                <TwoLine
                                    topRow={
                                        <div
                                            className={
                                                !cell.value.isEnabled ||
                                                props.title === UNLICENSED_USERS_TITLE
                                                    ? 'strikethrough'
                                                    : undefined
                                            }
                                        >
                                            {cell.value.name}
                                        </div>
                                    }
                                    bottomRow={email}
                                />
                            </Link>
                        </div>
                    );
                },
            },
            {
                id: 'roleName',
                Header: 'Role',
                // @ts-expect-error
                accessor: (row) => (row.role ? row.role.name : undefined),
                dataType: 'string',
                width: 80,
            },
            {
                id: 'teams',
                Header: 'Teams',
                // @ts-expect-error
                accessor: (row) => row.teams.map((team) => team.name).join(', '),
                dataType: 'string',
                width: 120,
            },
            {
                id: 'emailSync',
                Header: 'Email sync?',
                // @ts-expect-error
                accessor: (row) => row.hasMailSyncEnabled,
                dataType: 'boolean',
                width: 100,
                centerAlign: true,
                hasRightBorder: false,
                Cell: ({cell}: any) => {
                    if (cell.value) {
                        return <CheckmarkIcon size={16} alignCenter={true} />;
                    }

                    // Need to return something, but no icon needed if not enabled
                    return <div />;
                },
            },
            {
                id: 'lastSeen',
                Header: 'Last seen',
                // @ts-expect-error
                accessor: (row) => (row && row.lastSeen ? row.lastSeen : undefined),
                dataType: 'component',
                width: 100,
                centerAlign: true,
                Cell: ({cell}: any) => {
                    if (cell.value) {
                        const lastSeenDate = new Date(cell.value * 1000);
                        const now = new Date();
                        const format =
                            lastSeenDate.getFullYear() < now.getFullYear()
                                ? FormattedTimestamp.formats.monthDayYear
                                : FormattedTimestamp.formats.monthDay;

                        return (
                            <FormattedTimestamp
                                datetime={cell.value}
                                format={format}
                                showDetailedPopover={true}
                            />
                        );
                    }

                    return <div />;
                },
            },
        ];

        // @ts-expect-error
        const licenseColumns: any[] = licenses.map((license) => {
            return {
                id: license.licenseType,
                Header: () => (
                    <div>
                        <TwoLine
                            topRow={license.displayName}
                            bottomRow={
                                license.isPurchased || isTrial ? (
                                    <Body size='small' color='green'>
                                        {license.numUsed}/{license.numBought || '∞'}
                                    </Body>
                                ) : (
                                    <Body size='small' color='light'>
                                        Unavailable
                                    </Body>
                                )
                            }
                        />
                    </div>
                ),
                // @ts-expect-error
                accessor: (row) => row,
                Cell: ({cell}: any) => {
                    if (cell.value) {
                        const user = cell.value;
                        const isOwned = user.licenses.some(
                            // @ts-expect-error
                            (l) => l.licenseType === license.licenseType && l.isOwned
                        );

                        return (
                            <LicenseToggleComponent
                                user={cell.value}
                                licenseType={license.licenseType}
                                isOwned={isOwned}
                                refetchLicenseOverview={refetchLicenseOverview}
                                refetchUsers={refetchUsers}
                                isPurchased={license.isPurchased}
                                displayName={license.displayName}
                                isTrial={isTrial || false}
                            />
                        );
                    }
                },
                dataType: 'component',
                width: 100,
            };
        });

        if (sessionUser && sessionUser.isAdministrator) {
            licenseColumns.forEach((column, index) => {
                columnsArray.splice(LICENSE_COLUMNS_START_INDEX + index, 0, column);
            });
        }

        if (NutClientConfig.hasMFA) {
            columnsArray.splice(TWO_FACTOR_COLUMN_INDEX, 0, {
                id: 'twoFactor',
                Header: 'Two factor?',
                accessor: (row) => row.hasTwoFactorEnabled,
                dataType: 'boolean',
                width: 100,
                centerAlign: true,
                Cell: ({cell}: any) => {
                    if (cell.value) {
                        return <CheckmarkIcon size={16} alignCenter={true} />;
                    }

                    // Need to return something, but no icon needed if not enabled
                    return <div />;
                },
            });
        }

        return columnsArray;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, licenses, refetchLicenseOverview, refetchUsers, isTrial, sessionUser]);

    const addButtonRow = props.onAddRowClick ? (
        <AddButtonRow buttonText='Add a new user' onClick={props.onAddRowClick} />
    ) : undefined;

    return (
        <div className='mb-64'>
            <LegacySetupTableWrapper
                title={title}
                count={totalCount}
                actionMenuOptions={actionMenuOptions}
            >
                <LegacySetupTableContainer canLoadMore={hasNextPage}>
                    <Table
                        firstRow={addButtonRow}
                        isStriped={true}
                        fullWidth={true}
                        isResizable={true}
                        hasHeaderBorder={true}
                        hasHeaderCellBorders={true}
                        headerBackgroundColor='offWhite'
                        isCompact={true}
                        hasBorders={true}
                        disableSorting={true}
                        disableFilters={true}
                        // @ts-expect-error
                        columns={columns}
                        data={users}
                        specialStates={[
                            {
                                shouldRender: () => Boolean(errorMessage),
                                component: <ErrorState errorMessage={errorMessage} />,
                            },
                            {
                                shouldRender: () => isLoading && !users.length,
                                component: <LoadingPage padding={32} />,
                            },
                            {
                                shouldRender: () => !errorMessage && !isLoading && !users.length,
                                component: <LegacySetupTableEmptyState text={emptyStateMessage} />,
                            },
                        ]}
                    />
                    {hasNextPage ? (
                        <FetchMoreButton buttonText='Load more users' onClick={fetchMore} />
                    ) : undefined}
                </LegacySetupTableContainer>
            </LegacySetupTableWrapper>
        </div>
    );
}
